import { useState, useEffect, useCallback, useRef } from 'react';
import { getDataWithQueryString } from '@componentAPIs';
import { RMAXLogger } from '@RMAXLogger';
import { buildShipmentHistoryQueryParams } from '../utils';

interface UseShipmentRouteHistoryParams {
  planDate?: string;
  terminalCode?: string;
  scenarioId?: string;
  proId?: string | null;
  enabled?: boolean;
}

export interface ShipmentRouteHistoryItem {
  routeId?: string;
  routeName?: string;
  routeType?: string;
  createdDateTime?: string;
  category?: string;
  action?: string;
  createdByEmail?: string;
  totalDeliveryBills?: number;
  totalDeliveryStops?: number;
  totalTime?: string | number;
  anchorPointName?: string;
  isTrapRoute?: boolean | null;
  lockStatus?: number;
  stopSequence?: number;
}

interface ShipmentRouteHistoryData {
  numberOfPlanners?: number;
  numberOfRoutes?: number;
  shipmentRouteHistory?: ShipmentRouteHistoryItem[];
}

interface UseShipmentRouteHistoryResult {
  numberOfPlanners: number;
  numberOfRoutes: number;
  shipmentRouteHistory: ShipmentRouteHistoryItem[];
  loading: boolean;
  error: string | null;
}

const useShipmentRouteHistory = ({
  planDate,
  terminalCode,
  scenarioId,
  proId,
  enabled = true
}: UseShipmentRouteHistoryParams): UseShipmentRouteHistoryResult => {
  const [data, setData] = useState<ShipmentRouteHistoryData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const activeRequestRef = useRef(0);

  const fetchData = useCallback(async () => {
    if (!planDate || !terminalCode || !scenarioId || !proId) {
      return;
    }

    const requestId = ++activeRequestRef.current;

    setLoading(true);
    setError(null);

    try {
      const queryParams = buildShipmentHistoryQueryParams({
        planDate,
        terminalCode,
        scenarioId,
        proId
      });

      const response = await getDataWithQueryString('GetShipmentRouteHistory', queryParams);

      if (requestId !== activeRequestRef.current) return;

      if (response?.status === 200) {
        setData(response.data);
      } else {
        throw new Error('Failed to fetch shipment route history');
      }
    } catch (fetchError: any) {
      if (requestId !== activeRequestRef.current) return;

      const errorMessage = fetchError?.response?.data?.message || 'Failed to fetch history';
      setError(errorMessage);
      setData(null);

      RMAXLogger.error('Failed to fetch Shipment Route History', {
        proId,
        planDate,
        terminalCode,
        scenarioId,
        error: errorMessage
      });
    } finally {
      if (requestId === activeRequestRef.current) {
        setLoading(false);
      }
    }
  }, [planDate, terminalCode, scenarioId, proId]);

  useEffect(() => {
    if (enabled && proId) {
      fetchData();
    } else {
      activeRequestRef.current++;
      setData(null);
      setError(null);
      setLoading(false);
    }

    return () => {
      activeRequestRef.current++;
    };
  }, [fetchData, enabled, proId]);

  return {
    numberOfPlanners: data?.numberOfPlanners || 0,
    numberOfRoutes: data?.numberOfRoutes || 0,
    shipmentRouteHistory: data?.shipmentRouteHistory || [],
    loading,
    error
  };
};

export default useShipmentRouteHistory;
